<template>
  <component
    :is="linkType"
    v-if="!preventClick"
    class="ak-router-link"
    :href="renderedLink"
    v-bind="$attrs"
    @click.stop.prevent="handleClick"
  >
    <slot />
  </component>

  <component :is="linkType" v-else :href="renderedLink" v-bind="$attrs">
    <slot />
  </component>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { useRouter } from './hooks'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <router-link :to={ name: 'route', params: { id: 1234 } }>
   *  Click Me
   * </router-link>
   */
  name: 'RouterLink',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    type: {
      type: String,
      default: undefined,
    },

    to: {
      type: [String, Object],
      default: undefined,
    },

    preventClick: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const router = useRouter()

    /**
     * @type {String}
     * @description The type of link to render, this will default to an anchor.
     */
    const linkType = computed(() => {
      return props.type || 'a'
    })

    /**
     * @param {Object} opts - The options for the link.
     * @param {String} opts.name - The name of the route
     * @param {Object} opts.params - The params for the route
     * @returns {Object}
     * @description Returns the link properties for the requested route.
     */
    const linkProps = (opts = {}) => {
      let params = {}
      let name = ''

      if (typeof opts === 'string') {
        name = opts
      } else {
        name = opts.name
        params = opts.params
      }

      return {
        name,
        params,
      }
    }

    /**
     * @returns {String}
     * @description Returns the link for the requested route using
     * the angular JS routing functions.
     */
    const renderedLink = computed(() => {
      const { name, params } = linkProps(props?.to)
      return router.createHref(name, params)
    })

    /**
     * @description Executes the page change handler passed into
     * vue from angular JS, since we still need to let angular
     * handle the routing.
     */
    const handleClick = () => {
      const { name, params } = linkProps(props?.to)
      router.push(name, params)
    }

    return {
      renderedLink,
      handleClick,
      linkType,
    }
  },
})
</script>
